import type { StripeInvoicesList } from "@/API";
import { useCallback, useEffect, useRef, type FC } from "react";
import Text from "@/components/Text";
import InvoiceRow from "@/components/InvoiceRow";
import { useVirtualizer } from "@tanstack/react-virtual";
import { getListInvoices } from "@/store/listInvoicesState";
import { useTranslation } from "react-i18next";

type Props = {
  listInvoices: StripeInvoicesList | null;
  isfetchingListInvoices: boolean;
};

const Invoices: FC<Props> = ({ listInvoices, isfetchingListInvoices }) => {
  const { t } = useTranslation();

  const parentRef = useRef(null);

  const rowVirtualizer = useVirtualizer({
    count: listInvoices?.invoices.length ?? 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 80,
    measureElement:
      typeof window !== "undefined" &&
      navigator.userAgent.indexOf("Firefox") === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement && Array.isArray(listInvoices?.invoices)) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        if (
          scrollHeight - scrollTop - clientHeight < 500 &&
          !isfetchingListInvoices &&
          listInvoices.hasMore
        ) {
          getListInvoices({
            lastInvoiceId:
              listInvoices.invoices[listInvoices.invoices.length - 1]?.id,
          });
        }
      }
    },
    [isfetchingListInvoices, listInvoices],
  );

  useEffect(() => {
    fetchMoreOnBottomReached(parentRef.current);
  }, [fetchMoreOnBottomReached]);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-[16px]">
      <div className="flex flex-col gap-[16px]">
        <Text fontWeight={600} fontSize={20}>
          {t("billing")}
        </Text>
        <div
          onScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
          ref={parentRef}
          className="max-h-[620px] overflow-auto"
        >
          <div
            className="flex flex-col"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: "100%",
              position: "relative",
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualItem) => {
              const invoice = listInvoices?.invoices[virtualItem.index];
              if (!invoice) return null;

              return (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                    paddingBottom: 8,
                  }}
                  key={virtualItem.key}
                >
                  <InvoiceRow canDownload invoice={invoice} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invoices;
